import { ChevronRight } from "lucide-react";
import { useLocation } from "react-router";

import { selectCurrentUser } from "@/features/auth";
import { useAppSelector } from "@/shared/store/hooks";

import { LINK_TO_PATH, PATH_LABELS } from "./navConfig";

/* ════════════════════════════════════════════════════════════════════════════
   Breadcrumb dinâmico

   Resolve o `pathname` atual em `{ parent?, current }` percorrendo os menus que
   vieram do back (já filtrados por permissão). Para rotas fora da árvore de
   menus — `/meu-perfil`, sub-páginas internas — cai em `PATH_LABELS`. Se nada
   casar, não renderiza (evita rótulo "fantasma" em telas como NotFound).
════════════════════════════════════════════════════════════════════════════ */

interface BreadcrumbInfo {
  parent?: string;
  current: string;
}

function resolveBreadcrumb(
  pathname: string,
  menus: NonNullable<ReturnType<typeof selectCurrentUser>>["menus"] | undefined
): BreadcrumbInfo | null {
  // 1) Match exato em menu direto (Link próprio do menu, ex.: Dashboard)
  // 2) Match em submenu — usa o menu pai como `parent`
  for (const menu of menus ?? []) {
    if (menu.link && LINK_TO_PATH[menu.link] === pathname) {
      return { current: menu.nome };
    }
    for (const sub of menu.submenus ?? []) {
      if (LINK_TO_PATH[sub.link] === pathname) {
        return { parent: menu.nome, current: sub.nome };
      }
    }
  }

  // 3) Override estático para rotas fora do menu
  const override = PATH_LABELS[pathname];
  if (override) return override;

  return null;
}

export function Breadcrumb() {
  const location = useLocation();
  const user = useAppSelector(selectCurrentUser);
  const info = resolveBreadcrumb(location.pathname, user?.menus);

  if (!info) return null;

  return (
    <nav
      aria-label="breadcrumb"
      className="flex min-w-0 items-center gap-1.5"
    >
      {info.parent && (
        <>
          <span className="truncate text-[13px] font-medium text-gray-400">
            {info.parent}
          </span>
          <ChevronRight
            size={13}
            className="flex-shrink-0 text-gray-300"
            aria-hidden="true"
          />
        </>
      )}
      <span
        aria-current="page"
        className="truncate text-[13px] font-semibold text-gray-700"
      >
        {info.current}
      </span>
    </nav>
  );
}
